import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories } from '@/mocks/categories';
import { getVideosByCategory, getPopularVideos } from '@/mocks/videos';
import type { Video } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'hongtao');
  const [videos, setVideos] = useState<Video[]>([]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const allVideos = getVideosByCategory(activeCategory);
  const popularVideos = getPopularVideos(4);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (slug && categories.some((c) => c.slug === slug)) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    setVideos(getVideosByCategory(activeCategory));
  }, [activeCategory]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    window.scrollTo(0, 0);
    window.history.pushState(null, '', `/category/${newSlug}`);
  };

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">分类未找到</h2>
            <p className="text-foreground-600 mb-4">请选择其他视频平台分类</p>
            <Link to="/" className="inline-flex px-6 py-2 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const webPageSchema = {
    name: `${currentCategory.name} - 私密视频`,
    description: currentCategory.description,
    url: `https://www.simishipin.com/category/${activeCategory}`,
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <SchemaOrg type="WebPage" data={webPageSchema} />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: currentCategory.name },
          ]}
        />

        <section className="px-4 md:px-6 py-6 md:py-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-2">
                {currentCategory.name}
              </h1>
              <p className="text-sm md:text-base text-foreground-600">
                {currentCategory.description} — 高清画质不缓冲，全集资源一站畅享
              </p>
            </div>

            <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap flex items-center gap-1.5 cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            {allVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                {allVideos.map((video) => {
                  const catColor = categories.find((c) => c.slug === video.category)?.color || 'bg-primary-500';
                  const catIcon = categories.find((c) => c.slug === video.category)?.icon || 'ri-play-circle-line';
                  return (
                    <article key={video.id} className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300" data-product-shop>
                      <div className="relative aspect-video overflow-hidden">
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          title={`${video.title} - ${currentCategory.name}`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="px-2 py-1 bg-primary-500 text-white text-xs font-medium rounded-md whitespace-nowrap">
                            {video.quality}
                          </span>
                        </div>
                        <div className="absolute bottom-3 right-3">
                          <span className="px-2 py-1 bg-black/70 text-white text-xs rounded-md whitespace-nowrap">
                            {video.duration}
                          </span>
                        </div>
                      </div>
                      <div className="p-3 md:p-4">
                        <h3 className="text-sm md:text-base font-medium text-foreground-900 line-clamp-2 leading-snug mb-2">
                          {video.title}
                        </h3>
                        <div className="flex items-center justify-between text-xs text-foreground-500">
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {video.views >= 10000 ? `${(video.views / 10000).toFixed(1)}万` : video.views}
                          </span>
                          <span className="px-2 py-0.5 bg-background-100 rounded-full text-foreground-500 whitespace-nowrap">
                            {currentCategory.name}
                          </span>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
                  <i className="ri-video-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">该分类暂无视频内容，请查看其他分类</p>
              </div>
            )}
          </div>
        </section>

        {popularVideos.length > 0 && (
          <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                全网热门推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                {popularVideos.map((video) => (
                  <article key={video.id} className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300" data-product-shop>
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        title={video.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute bottom-3 right-3">
                        <span className="px-2 py-1 bg-black/70 text-white text-xs rounded-md whitespace-nowrap">
                          {video.duration}
                        </span>
                      </div>
                    </div>
                    <div className="p-3 md:p-4">
                      <h3 className="text-sm md:text-base font-medium text-foreground-900 line-clamp-2 leading-snug mb-2">
                        {video.title}
                      </h3>
                      <div className="flex items-center gap-1 text-xs text-foreground-500">
                        <i className="ri-eye-line"></i>
                        {video.views >= 10000 ? `${(video.views / 10000).toFixed(1)}万` : video.views}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}